import { useState } from 'react';
import { testimonials, reviewSummary } from '../../../mocks/testimonials';

const serviceColors: Record<string, string> = {
  'Hardwood Refinishing': 'bg-amber-50 text-amber-700',
  'Hardwood Installation': 'bg-green-50 text-green-700',
  'Floor Repair': 'bg-sky-50 text-sky-700',
  'Floor Replacement': 'bg-violet-50 text-violet-700',
};

export default function Testimonials() {
  const [active, setActive] = useState(0);

  return (
    <section className="bg-white py-28 px-6 relative overflow-hidden">
      {/* Subtle bg accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full -translate-y-1/2 translate-x-1/3" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-sand rounded-full translate-y-1/2 -translate-x-1/3" />

      <div className="relative max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="font-sans text-xs uppercase tracking-widest text-oak font-semibold">Client Stories</span>
          <h2 className="font-serif text-4xl md:text-5xl text-walnut font-bold mt-3">What Maryland Homeowners Say</h2>
          <div className="flex items-center justify-center gap-6 mt-5 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[...Array(4)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-gold text-lg" />
                ))}
                <i className="ri-star-half-fill text-gold text-lg" />
              </div>
              <span className="text-charcoal/70 font-sans text-sm font-semibold">{reviewSummary.google.rating} on Google</span>
              <span className="text-charcoal/45 font-sans text-xs">({reviewSummary.google.count} reviews)</span>
            </div>
            <div className="w-px h-4 bg-charcoal/20" />
            <div className="flex items-center gap-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-gold text-base" />
                ))}
              </div>
              <span className="text-charcoal/70 font-sans text-sm font-semibold">{reviewSummary.homeAdvisor.rating} on HomeAdvisor</span>
              <span className="text-charcoal/45 font-sans text-xs">({reviewSummary.homeAdvisor.count}+ reviews)</span>
            </div>
          </div>
        </div>

        {/* Featured testimonial */}
        <div className="bg-sand rounded-3xl p-10 md:p-14 mb-8 relative overflow-hidden">
          <div className="absolute top-8 left-10 text-gold/20 font-serif text-[8rem] leading-none font-bold select-none">&ldquo;</div>
          <div className="relative max-w-3xl mx-auto text-center">
            <p className="font-serif text-xl md:text-2xl text-walnut font-medium leading-relaxed mb-8 italic">
              &ldquo;{testimonials[active].quote}&rdquo;
            </p>
            <div className="flex items-center justify-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-walnut rounded-full flex-shrink-0">
                <i className="ri-user-3-line text-gold text-xl" />
              </div>
              <div className="text-left">
                <div className="font-sans text-base font-bold text-walnut">{testimonials[active].author}</div>
                <div className="font-sans text-sm text-charcoal/50">{testimonials[active].location}</div>
              </div>
              <div className={`ml-2 px-3 py-1 rounded-full text-xs font-semibold font-sans ${serviceColors[testimonials[active].service] || 'bg-gold/10 text-walnut'}`}>
                {testimonials[active].service}
              </div>
            </div>
          </div>
        </div>

        {/* Grid Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {testimonials.map((t, i) => (
            <button
              key={t.id}
              onClick={() => setActive(i)}
              className={`text-left rounded-2xl p-6 border transition-all duration-200 cursor-pointer ${
                active === i
                  ? 'bg-walnut border-walnut'
                  : 'bg-white border-sand-dark hover:border-oak/30'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, j) => (
                    <i key={j} className={`ri-star-fill text-sm ${active === i ? 'text-gold' : 'text-gold'}`} />
                  ))}
                </div>
                <span className={`text-xs font-sans font-medium px-2.5 py-1 rounded-full ${active === i ? 'bg-white/15 text-white' : (serviceColors[t.service] || 'bg-gold/10 text-walnut')}`}>
                  {t.service}
                </span>
              </div>
              <p className={`font-sans text-sm leading-relaxed line-clamp-3 mb-4 ${active === i ? 'text-white/85' : 'text-charcoal/70'}`}>
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="flex items-center gap-2.5">
                <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${active === i ? 'bg-white/20' : 'bg-walnut/10'}`}>
                  <i className={`ri-user-3-line text-sm ${active === i ? 'text-white' : 'text-walnut'}`} />
                </div>
                <div>
                  <div className={`font-sans text-xs font-bold ${active === i ? 'text-white' : 'text-walnut'}`}>{t.author}</div>
                  <div className={`font-sans text-xs flex items-center gap-1 ${active === i ? 'text-white/50' : 'text-charcoal/40'}`}>
                    {t.location} · <i className="ri-google-line text-xs" /> {t.source}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* CTA After Testimonials */}
        <div className="text-center pt-4">
          <p className="font-sans text-charcoal/60 text-sm mb-6">Join hundreds of Maryland homeowners who trust Premier Hardwood Floors.</p>
          <a
            href="tel:+14105550192"
            className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-walnut text-white font-semibold rounded-xl hover:bg-walnut-dark transition-colors font-sans text-sm whitespace-nowrap"
          >
            <i className="ri-phone-fill" /> Call Us Today — (410) 555-0192
          </a>
        </div>
      </div>
    </section>
  );
}
